import { inject, Injectable, Signal } from '@angular/core'
import { from, Observable, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take, tap } from 'rxjs/operators'
import { AppUser } from '../../../../domain/user/app-user.model'
import { AppUsersFirestoreService } from '../../core/firestore-services/app-users-firestore.service'
import { NavigationService } from '../../core/services/navigation.service'
import { FirestoreService } from '../../shared/modules/fire/firestore.service'

import { Auth, authState, getIdTokenResult, signInWithEmailAndPassword, user, User } from '@angular/fire/auth'
import { DestroyAware } from '../../shared/utils/destroy-aware.decorator'
import { toSignal } from '@angular/core/rxjs-interop'

@DestroyAware()
@Injectable({ providedIn: 'root' })
export class AuthService {
	private readonly auth = inject(Auth)
	private readonly userFirestoreService = inject<FirestoreService<AppUser>>(AppUsersFirestoreService)
	private readonly navigationService = inject<NavigationService>(NavigationService)

	static REDIRECT_AFTER_AUTH_URL: string | null = null
	userUid: string | null = null
	readonly firebaseUser$ = user(this.auth)
	readonly appUserChanges$: Observable<AppUser | null> = authState(this.auth).pipe(
		distinctUntilChanged(),
		tap((user) => (this.userUid = user?.uid ?? null)),
		switchMap((user) => (user ? this.findAppUser(user) : of(null))),
		shareReplay(1),
	)
	readonly appUser$: Observable<AppUser | null> = this.appUserChanges$.pipe(take(1))
	readonly appUser: Signal<AppUser | null> = toSignal(this.appUserChanges$, { initialValue: null })

	signInWithEmailAndPassword(email: string, password: string): Observable<unknown> {
		return from(signInWithEmailAndPassword(this.auth, email, password))
	}

	logout(redirectToSignIn: boolean = true, url: string = ''): Promise<unknown> {
		return this.auth.signOut().then(() => {
			this.userUid = null
			if (redirectToSignIn) {
				return this.navigationService.goToLogin(url)
			}
			return null
		})
	}

	hasAnyClaim(claims: Array<string>): Observable<boolean> {
		return this.firebaseUser$.pipe(
			switchMap((user) => (user ? getIdTokenResult(user) : of(null))),
			map((tokenResult) => (tokenResult ? this.isMatchingAnyClaim(tokenResult.claims, claims) : false)),
		)
	}

	private findAppUser(user: User): Observable<AppUser | null> {
		return this.userFirestoreService.observeValues(user.uid).pipe(
			map((value) => value as AppUser),
			catchError(() => of(null)),
		)
	}

	private isMatchingAnyClaim(userClaims: { [key: string]: unknown }, claimsToMatch: Array<string>): boolean {
		return Object.keys(userClaims).some((claim) => claimsToMatch.includes(claim))
	}
}
